import { useRef, useState } from 'react';
import { notification } from 'antd';
import { unwrapResult } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, CombinedStates } from 'core/types';
import { getVisitRequestReview, scheduleRequest } from 'core/visitRequestReview/visitRequestReviewApi';
import { groupShiftsByShift, ShiftGroup } from 'core/visitRequestReview/scheduleShifts';
import { ActionDataItem, TableFilters, VisitorRequestReview, VisitRequestReviewResponsePayload } from 'core/visitRequestReview/types';
import { ScheduleCompletePayload } from 'pages/VisitorRequestView/components/ScheduleModal';

// The schedule endpoint filters the request's dates by this; an empty filter
// pulls every shift so the officer can be assigned to any of them.
const EMPTY_FILTERS: TableFilters = {
    searchTerms: [],
    startDate: '',
    endDate: '',
    siteText: [],
    statusText: [],
    sortDirection: null,
};

// The minimum a report row needs to be schedulable inline. `encryptedId` is a
// required string property — the API always addresses requests by their string
// hash id, so keeping this string-only (rather than string | number) keeps every
// call site consistent. Its value may be empty for a not-yet-persisted row, which
// openSchedule guards against.
export interface SchedulableRow {
    encryptedId: string | null;
}

export interface InlineSchedule<T extends SchedulableRow> {
    scheduleRow: T | null;
    shiftGroups: ShiftGroup[];
    securityTeam: ActionDataItem[] | null;
    // Retail requests name their officer by hand — no saved-officer dropdown.
    isRetail: boolean;
    loading: boolean;
    openSchedule: (row: T) => void;
    closeSchedule: () => void;
    handleScheduleComplete: (payload: ScheduleCompletePayload) => void;
}

// Encapsulates the "assign officers without leaving the list" flow — loading a
// request's shifts, holding modal state, and submitting the assignment — so the
// report page stays focused on presentation. Pass an onScheduled callback to
// refresh the caller's list after a successful assignment.
export function useInlineSchedule<T extends SchedulableRow>(onScheduled: () => void): InlineSchedule<T> {
    const dispatch = useDispatch<AppDispatch>();
    const { isScheduling, isFetching } = useSelector((state: CombinedStates) => state.visitRequestReview);

    // The request currently being scheduled inline (null = modal closed).
    const [scheduleRow, setScheduleRow] = useState<T | null>(null);

    // A local snapshot of the loaded request's review data. We deliberately hold
    // this per-modal rather than reading the shared `visitRequestReview` Redux
    // slice on every render: that slice is also used by the full review screen, so
    // reading it live would let another consumer's fetch leak stale/wrong shifts
    // into an open modal. The snapshot is taken from the fetch's own payload.
    const [reviewData, setReviewData] = useState<VisitorRequestReview | null>(null);

    // A monotonic token identifying the current open request. Each openSchedule
    // stamps a fresh token; only the fetch whose token still matches is allowed to
    // populate the modal. Closing (or scheduling) bumps the token, which cancels
    // any in-flight fetch. Using a per-invocation counter rather than the request
    // id also covers the "close, then reopen the SAME row before the first fetch
    // resolves" race — the first resolve is stamped with a now-stale token.
    const requestToken = useRef(0);

    // Load the row's shifts + officer list, then open the modal.
    const openSchedule = (row: T): void => {
        if (!row.encryptedId) {
            return;
        }
        requestToken.current += 1;
        const token = requestToken.current;
        dispatch(getVisitRequestReview({ id: row.encryptedId, filters: EMPTY_FILTERS }))
            .then(unwrapResult)
            .then((payload: VisitRequestReviewResponsePayload) => {
                // A newer open, a close, or a completed schedule superseded this
                // fetch — drop it so we never show stale data for the wrong request.
                if (requestToken.current !== token) {
                    return;
                }
                setReviewData(payload.visitRequest);
                setScheduleRow(row);
            })
            // The thunk already surfaces a specific API error notification; here we
            // only need to keep the modal closed and swallow the rejection.
            .catch(() => undefined);
    };

    const closeSchedule = (): void => {
        // Invalidate any in-flight fetch so a late resolve can't reopen the modal.
        requestToken.current += 1;
        setReviewData(null);
        setScheduleRow(null);
    };

    const handleScheduleComplete = (payload: ScheduleCompletePayload): void => {
        if (!scheduleRow?.encryptedId) {
            return;
        }
        if (payload.shifts.length === 0) {
            notification.warning({ message: 'Warning', description: 'Please select at least one shift.' });
            return;
        }
        dispatch(scheduleRequest({ id: scheduleRow.encryptedId, data: { shifts: payload.shifts } }))
            .then(unwrapResult)
            .then(() => {
                // Invalidate any in-flight load fetch before closing.
                requestToken.current += 1;
                setReviewData(null);
                setScheduleRow(null);
                onScheduled();
            })
            // The thunk already surfaces a specific API error notification.
            .catch(() => undefined);
    };

    return {
        scheduleRow,
        shiftGroups: groupShiftsByShift(reviewData?.dates ?? null),
        securityTeam: reviewData?.securityOfficers ?? null,
        isRetail: !!reviewData?.isRetail,
        loading: isScheduling || isFetching,
        openSchedule,
        closeSchedule,
        handleScheduleComplete,
    };
}
